/**
 * Migration Service
 * One-time migration utility to move pages from board JSONB to pages table
 * 
 * @module services/migrationService
 */

import { supabase } from './supabase';
import { Page } from '../types';
import { getCurrentUserId } from './authService';
import { migratePages, hasMigratedPages } from './pageService';
import { logger } from '../lib/logger';

// ============================================================================
// Types
// ============================================================================

export interface MigrationResult {
    success: boolean;
    boardId: string;
    pagesMigrated: number;
    error?: string;
}

export interface MigrationProgress {
    total: number;
    completed: number;
    current: string | null;
    results: MigrationResult[];
}

type ProgressCallback = (progress: MigrationProgress) => void;

// ============================================================================
// Migration Functions
// ============================================================================

/**
 * Check if a board needs migration (has pages in content but not in pages table)
 */
async function needsMigration(boardId: string, contentPages?: Page[]): Promise<boolean> {
    // Already migrated?
    const alreadyMigrated = await hasMigratedPages(boardId);
    if (alreadyMigrated) return false;

    // Has pages in content to migrate?
    return Boolean(contentPages && contentPages.length > 0);
}

/**
 * Migrate a single board's pages from content to pages table
 */
export async function migrateSingleBoard(boardId: string): Promise<MigrationResult> {
    try {
        // Get board with content
        const { data, error } = await supabase
            .from('boards')
            .select('id, content')
            .eq('id', boardId)
            .single();

        if (error || !data) {
            return {
                success: false,
                boardId,
                pagesMigrated: 0,
                error: error?.message || 'Board not found'
            };
        }

        const content = data.content || {};
        const pages: Page[] = content.pages || [];

        // Check if migration needed
        const needsMig = await needsMigration(boardId, pages);
        if (!needsMig) {
            return {
                success: true,
                boardId,
                pagesMigrated: 0
            };
        }

        // Migrate pages to new table
        const success = await migratePages(boardId, pages);
        if (!success) {
            return {
                success: false,
                boardId,
                pagesMigrated: 0,
                error: 'Failed to insert pages into pages table'
            };
        }

        // Optionally clear pages from board content (keep for safety rollback)
        // For now, we leave the content intact as backup

        return {
            success: true,
            boardId,
            pagesMigrated: pages.length
        };
    } catch (error) {
        return {
            success: false,
            boardId,
            pagesMigrated: 0,
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
}

/**
 * Migrate all boards for the current user
 */
export async function migrateAllBoards(
    onProgress?: ProgressCallback
): Promise<MigrationResult[]> {
    const userId = getCurrentUserId();
    if (!userId) {
        logger.warn('[Migration] No user ID');
        return [];
    }

    // Get all boards for user
    const { data: boards, error } = await supabase
        .from('boards')
        .select('id, content')
        // RLS handles user filtering
        .not('content', 'is', null);

    if (error || !boards) {
        logger.error('[Migration] Failed to fetch boards', error);
        return [];
    }

    const results: MigrationResult[] = [];
    const progress: MigrationProgress = {
        total: boards.length,
        completed: 0,
        current: null,
        results: []
    };

    for (const board of boards) {
        progress.current = board.id;
        onProgress?.(progress);

        const result = await migrateSingleBoard(board.id);
        results.push(result);
        progress.results.push(result);
        progress.completed++;

        onProgress?.(progress);
    }

    progress.current = null;
    onProgress?.(progress);

    const successCount = results.filter(r => r.success).length;
    const totalPages = results.reduce((sum, r) => sum + r.pagesMigrated, 0);

    logger.info(`[Migration] Complete: ${successCount}/${boards.length} boards, ${totalPages} total pages migrated`);

    return results;
}

/**
 * Check migration status for all user boards
 */
export async function getMigrationStatus(): Promise<{
    total: number;
    migrated: number;
    pending: number;
    boards: Array<{
        id: string;
        title: string;
        migrated: boolean;
        pagesInContent: number;
    }>;
}> {
    const { data: boards, error } = await supabase
        .from('boards')
        .select('id, title, content');

    if (error || !boards) {
        return { total: 0, migrated: 0, pending: 0, boards: [] };
    }

    const statuses = await Promise.all(
        boards.map(async (board) => {
            const content = board.content || {};
            const pagesInContent = (content.pages || []).length;
            const migrated = await hasMigratedPages(board.id);

            return {
                id: board.id,
                title: board.title,
                migrated,
                pagesInContent
            };
        })
    );

    return {
        total: statuses.length,
        migrated: statuses.filter(s => s.migrated).length,
        pending: statuses.filter(s => !s.migrated && s.pagesInContent > 0).length,
        boards: statuses
    };
}

/**
 * Run migration on app startup if needed (silent)
 */
export async function autoMigrateIfNeeded(): Promise<void> {
    const status = await getMigrationStatus();

    if (status.pending > 0) {
        logger.info(`[Migration] Auto-migrating ${status.pending} boards...`);
        await migrateAllBoards();
    }
}
